import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from "@angular/core";
import {ActionsSubject, Store} from "@ngrx/store";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {AbstractIndexComponent} from "../../utility/abstract-index/abstract-index.component";
import {StorageLocationPartialState} from "../../base-data/storage-location/+state/storage-location.reducer";
import {BookingArticle, BookingTypeEnum, StorageLocation, StorageLocationArticle} from "@knust/api-interfaces";
import {BehaviorSubject, combineLatestWith, debounceTime, filter, map, Observable, ReplaySubject, take} from "rxjs";
import {AbstractIndexCommands} from "../../utility/abstract-index/abstract-index-commands";
import {SelectionModel} from "@angular/cdk/collections";
import {
  addItemsToCart,
  addItemsToCartSuccess,
  addItemsToTrunk,
  addItemsToTrunkSuccess,
  changeItemsInTrunk,
  closeTrunk,
} from '../../trunks/+state/trunk.actions';
import {ColumnConfig} from "./column-config";
import {FormBuilder, FormControl, UntypedFormGroup} from "@angular/forms";
import {ofType} from "@ngrx/effects";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {AuthService} from "../../utility/services/auth.service";
import {
  calculateNextIntervalStep
} from "../../utility/functions/change-to-next-interval-step.function";
import {NotificationService} from "../../utility/services/notification.service";
import {StorageLocationService} from "../../utility/services/storage-location.service";
import { MatDialog } from '@angular/material/dialog';

@UntilDestroy()
@Component({
    selector: 'knust-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DataTableComponent
  extends AbstractIndexComponent<StorageLocation, StorageLocationPartialState>
  implements OnInit
{
  buyStockInputs: number[] = [];
  takeStockInputs: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(
    []
  );
  takeStockNotes: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  storageLocationFilterCtrl: FormControl = new FormControl();
  storageLocationSearchTerm = '';
  public filteredStorageLocations: ReplaySubject<StorageLocation[]> =
    new ReplaySubject<StorageLocation[]>(1);

  buyStockForm: UntypedFormGroup = this.fb.group({});

  bookingsForm: UntypedFormGroup = this.fb.group({});
  protected readonly BookingTypeEnum = BookingTypeEnum;

  @Input() disableFilterblock = false;
  @Input() disableNewButton = false;
  @Input() disableEditing = false;
  @Input() newButtonText = 'Neues Element hinzufügen';
  @Input() noDataText = 'Es wurden keine Daten gefunden.';
  @Input() enableExpandableRows = false;
  @Input() expandedColumns: string[] = [];
  @Input() allRowsExpanded = false;
  expandedElements: any[] = [];
  @Input() enableSelection = false;
  @Input() useClickAsSelection = false;
  @Input() propagateClicks = false;
  @Input() initialSelection: any[] = [];
  @Input() allowMultiSelect = true;
  @Input() hasMasterCheckbox = false;
  @Input() selectionHeaderText = 'Hinzugefügt';
  selection = new SelectionModel<any>(
    this.allowMultiSelect,
    this.initialSelection
  );
  @Input() initialStorageLocations!: Observable<StorageLocation[] | null>;
  @Input() selectedStorageLocation!: BehaviorSubject<number | null>;
  @Input() selectedArticles!: BehaviorSubject<BookingArticle[] | null>;
  @Input() bookingType!: Observable<BookingTypeEnum | null>;
  @Input() completed = false;
  @Input() disablePagination = false;

  @Input() columnConfig: ColumnConfig[] = [];

  @Input() goToLastPage!: Observable<void>;

  @Output() entityRowClick = new EventEmitter<any>();
  @Output() addEntityClick = new EventEmitter<any>();
  @Output() addEmptyEntityClick = new EventEmitter<any>();
  @Output() deleteEntityClick = new EventEmitter<any>();
  @Output() restoreEntityClick = new EventEmitter<any>();
  @Output() toggleEntityClick = new EventEmitter<any>();
  @Output() entityActionClick = new EventEmitter<any>();
  @Output() duplicateEntityClick = new EventEmitter<any>();
  @Output() filterAddedEvent = new EventEmitter<any>();
  @Output() languageFilterChangedEvent = new EventEmitter<any>();
  @Output() selectionUpdated = new EventEmitter<any[]>();
  @Output() inputChanged = new EventEmitter<any>();
  @Output() notesChanged = new EventEmitter<any>();
  @Output() closeTrunkClick = new EventEmitter<any>();
  @Output() submitBookingsClick = new EventEmitter<any>();
  @Output() addArticleToTrunk = new EventEmitter<any>();

  @Input() commandMap!: AbstractIndexCommands<any>;

  @Input() displayedColumns: string[] = [];

  constructor(
    protected override store: Store<StorageLocationPartialState>,
    private fb: FormBuilder,
    private actionsSubject: ActionsSubject,
    public authService: AuthService,
    private notificationService: NotificationService,
    private storageLocationService: StorageLocationService,
    private dialog: MatDialog
  ) {
    super(store);
  }

  override ngOnInit() {
    super.ngOnInit();

    if (this.displayedColumns.includes('buyStock') || this.displayedColumns.includes('buyStockInTrunk')) {
      this.dataSource.connect().subscribe((articleStorageLocations) => {
        for (const articleStorageLocation of articleStorageLocations as unknown as StorageLocationArticle[]) {
          if (articleStorageLocation.id) {
            // Vorhandene Zeilen behalten, damit eingegebene Mengen beim Neuladen (Blättern, Suche) erhalten bleiben
            const existingFormGroup = this.buyStockForm.get(
              articleStorageLocation.id + ''
            );
            const formGroup =
              existingFormGroup ??
              this.fb.group({
                articleStorageLocation,
                amount: null,
              });

            if (existingFormGroup) {
              // Bestand und Entnahmeschritt aktuell halten, eine Menge über dem neuen Bestand wird angepasst
              existingFormGroup.patchValue({ articleStorageLocation });

              const amountControl = existingFormGroup.get('amount');

              if (amountControl && amountControl.value !== null) {
                amountControl.patchValue(
                  normalizeStockAmount(
                    parseInt(amountControl.value, 10),
                    articleStorageLocation.article?.samplingStep ?? 1,
                    articleStorageLocation.actualStock
                  )
                );
              }
            }

            if (articleStorageLocation.actualStock === 0) {
              formGroup.get('amount')?.disable();
            } else {
              formGroup.get('amount')?.enable();
            }


            if (!existingFormGroup) {
              this.buyStockForm.addControl(
                articleStorageLocation.id + '',
                formGroup
              );
            }
          }
        }

      });

      this.actionsSubject
        .pipe(untilDestroyed(this), ofType(addItemsToCartSuccess, addItemsToTrunkSuccess))
        .subscribe(() => this.buyStockForm.reset());
    }

    if (this.displayedColumns.includes('bookings')) {
      this.initialStorageLocations
        .pipe(
          combineLatestWith(this.selectedStorageLocation),
          filter(
            ([storageLocations, selectedLocationID]) =>
              !!selectedLocationID &&
              !!storageLocations &&
              storageLocations.length > 0
          ),
          take(1)
        )
        .subscribe(([initialStorageLocations, selectedLocationID]) => {
          if (initialStorageLocations) {
            this.filteredStorageLocations.next(
              initialStorageLocations.filter(
                (storageLocation) => storageLocation.id !== selectedLocationID
              )
            );
          }
        });

      this.storageLocationFilterCtrl.valueChanges
        .pipe(
          untilDestroyed(this),
          debounceTime(500),
          filter((value) => value !== this.storageLocationSearchTerm)
        )
        .subscribe(() => {
          this.filterStorageLocations();
        });

      this.dataSource
        .connect()
        .pipe(
          combineLatestWith(this.selectedArticles),
          filter(
            ([storageLocationArticles, bookingArticles]) =>
              bookingArticles !== null
          )
        )
        .subscribe(([storageLocationArticles, bookingArticles]) => {
          // Das Formular bleibt bestehen, damit Eingaben beim Neuladen (z. B. nach einer Suche) erhalten bleiben

          for (const storageLocationArticle of storageLocationArticles as unknown as StorageLocationArticle[]) {
            if (storageLocationArticle.id) {
              let foundArticleInBookingsIndex = -1;

              if (bookingArticles) {
                foundArticleInBookingsIndex = bookingArticles.findIndex(
                  (bookingArticle) =>
                    bookingArticle.storageArticle.id ===
                    storageLocationArticle.id
                );
              }


              const bookingArticleValues =
                bookingArticles && foundArticleInBookingsIndex > -1
                  ? {
                      amount: bookingArticles[foundArticleInBookingsIndex].amount,
                      receivingStorageLocation:
                        bookingArticles[foundArticleInBookingsIndex]
                          .receivingStorageArticle?.storageLocation.id,
                      note: bookingArticles[foundArticleInBookingsIndex].note,
                    }
                  : null;
              const existingFormGroup = this.bookingsForm.get(
                storageLocationArticle.id + ''
              );

              if (existingFormGroup) {
                // Vorhandene Eingaben haben Vorrang, leere Zeilen werden mit den Buchungsartikeln befüllt
                if (
                  bookingArticleValues &&
                  !this.hasBookingRowInput(existingFormGroup.value)
                ) {
                  existingFormGroup.patchValue(bookingArticleValues);
                }
              } else {
                this.bookingsForm.addControl(
                  storageLocationArticle.id + '',
                  this.fb.group({
                    storageLocationArticle,
                    amount: [bookingArticleValues?.amount ?? null],
                    receivingStorageLocation:
                      bookingArticleValues?.receivingStorageLocation ?? null,
                    note: bookingArticleValues?.note ?? null,
                  })
                );
              }
            }
          }


          if (this.completed) {
            this.bookingsForm.disable();
          }
        });
    }

    this.takeStockInputs
      .pipe(untilDestroyed(this))
      .subscribe((takeStockInputs) => this.inputChanged.next(takeStockInputs));

    this.takeStockNotes
      .pipe(untilDestroyed(this))
      .subscribe((takeStockNotes) => this.notesChanged.next(takeStockNotes));
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected == numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  new() {
    this.addEntityClick.emit();
  }

  delete(entity: any) {
    this.deleteEntityClick.emit(entity);
  }

  entityClicked(event: any) {
    this.entityRowClick.emit(event);
  }

  bookStock() {
    if (this.bookingsForm.valid) {
      const bookingInputs = this.bookingsForm.value;
      const submitBookings: {
        storageLocationArticleId: number;
        amount: number;
        note?: string;
        receivingStorageLocation?: number;
      }[] = [];

      this.bookingType
        .pipe(
          filter((type) => !!type),
          take(1)
        )
        .subscribe((bookingType) => {
          let missingReceivingStorageLocation = false;

          Object.keys(bookingInputs).forEach((storageLocationArticleId) => {
            if (
              bookingInputs[storageLocationArticleId].amount &&
              bookingInputs[storageLocationArticleId].amount > 0
            ) {
              if (
                bookingType !== BookingTypeEnum.MOVE ||
                bookingInputs[storageLocationArticleId].receivingStorageLocation
              ) {
                submitBookings.push({
                  storageLocationArticleId: parseInt(
                    storageLocationArticleId,
                    10
                  ),
                  amount: bookingInputs[storageLocationArticleId].amount,
                  receivingStorageLocation:
                    bookingInputs[storageLocationArticleId]
                      .receivingStorageLocation,
                  note: bookingInputs[storageLocationArticleId].note,
                });
              } else {
                missingReceivingStorageLocation = true;
              }
            }
          });

          if (missingReceivingStorageLocation) {
            // Einmalige Meldung, es wird nichts gebucht, damit keine Eingaben verloren gehen
            this.notificationService.createNotification({
              title: 'Fehlende Angaben',
              text: 'Für jeden Artikel muss die Anzahl und der empfangende Lagerort angegeben werden.',
              status: 'error',
              dismissed: 0,
            });
          } else if (submitBookings.length > 0) {
            this.submitBookingsClick.emit(submitBookings);

            this.bookingsForm.reset();
          } else {
            this.notificationService.createNotification({
              title: 'Keine Artikel ausgewählt',
              text: 'Es muss mindestens eine Einheit von einem Artikel ausgewählt werden.',
              status: 'error',
              dismissed: 0,
            });
          }
        });
    } else {
      this.notifyInvalidForm(this.bookingsForm);
    }
  }

  /** Ob im Buchungsformular mindestens eine Anzahl eingetragen ist */
  hasBookingInputs(): boolean {
    return Object.values(this.bookingsForm.controls).some(
      (formGroup) => formGroup.value?.amount > 0
    );
  }

  private hasBookingRowInput(rowValue: {
    amount?: number | null;
    receivingStorageLocation?: number | null;
    note?: string | null;
  }): boolean {
    return (
      (rowValue.amount !== null && rowValue.amount !== undefined) ||
      !!rowValue.receivingStorageLocation ||
      !!rowValue.note
    );
  }

  private notifyInvalidForm(form: UntypedFormGroup) {
    form.markAllAsTouched();

    this.notificationService.createNotification({
      title: 'Ungültige Angaben',
      text: 'Bitte die markierten Eingaben prüfen. Es kann höchstens der verfügbare Bestand ausgewählt werden.',
      status: 'error',
      dismissed: 0,
    });
  }

  changeStockInputToNextIntervalStep(samplingStep: number, rowId: number) {
    const rowFormGroup = this.buyStockForm.get([rowId]);
    const stockInput = rowFormGroup?.get('amount');

    if (stockInput) {
      const availableStock =
        rowFormGroup?.get('articleStorageLocation')?.value?.actualStock ??
        Infinity;
      const amount = normalizeStockAmount(
        parseInt(stockInput.value, 10),
        samplingStep,
        availableStock
      );

      if (amount !== stockInput.value) {
        stockInput.patchValue(amount);
      }
    }
  }

  changeTakeStockInputToNextIntervalStep(
    event: any,
    index: number,
    samplingStep: number,
    minStock: number,
    maxStock: number
  ) {
    const targetNumberValue = parseInt(event.target.value, 10);

    if (Number.isNaN(targetNumberValue) || targetNumberValue === 0) {
      return;
    }

    const step = samplingStep > 0 ? samplingStep : 1;
    // Auf den nächsten Schritt runden, aber nur innerhalb der erlaubten Grenzen
    const amount = Math.max(
      Math.min(
        calculateNextIntervalStep(targetNumberValue, step),
        Math.floor(maxStock / step) * step
      ),
      Math.ceil(minStock / step) * step
    );

    if (amount !== targetNumberValue) {
      event.target.value = amount;
    }

    this.takeStockInputs.value[index] = amount;
  }

  buyStocks(toTrunk?: boolean) {
    if (this.buyStockForm.valid) {
      const buyStockInputs = this.buyStockForm.value;
      const submitBuyStocks: {
        storageLocationArticleId: number;
        amount: number;
      }[] = [];

      Object.keys(buyStockInputs).forEach((storageLocationArticleId) => {
        if (
          buyStockInputs[storageLocationArticleId].amount &&
          buyStockInputs[storageLocationArticleId].amount > 0
        ) {
          submitBuyStocks.push({
            storageLocationArticleId: parseInt(storageLocationArticleId, 10),
            amount: buyStockInputs[storageLocationArticleId].amount,
          });
        }
      });

      if (submitBuyStocks.length > 0) {
        if (!toTrunk) {
          this.store.dispatch(addItemsToCart({ items: submitBuyStocks }));
        } else {
          this.store.dispatch(addItemsToTrunk({ items: submitBuyStocks }));
        }
      } else {
        this.notificationService.createNotification({
          title: 'Kein Artikel ausgewählt',
          text: 'Es muss mindestens eine Einheit von einem Artikel ausgewählt werden.',
          status: 'error',
          dismissed: 0,
        });
      }
    } else {
      this.notifyInvalidForm(this.buyStockForm);
    }
  }

  buyStock(index: number) {
    const storageLocationArticle = this.dataSource.connect().value[
      index
    ] as any;

    const item = {
      storageLocationArticleId: storageLocationArticle.id,
      amount: this.buyStockInputs[index],
    };

    // this.store.dispatch(addItemToCart({ item }));
  }

  private getChangedItemsInTrunk() {
    let index = 0;
    const changedItems: {
      trunkArticleId: number;
      amount: number;
      note?: string;
    }[] = [];

    for (const takeInput of this.takeStockInputs.value) {
      if (takeInput) {
        const trunkArticle = this.dataSource.connect().value[index] as any;

        const item: { trunkArticleId: number; amount: number; note?: string } =
          {
            trunkArticleId: trunkArticle.id,
            amount: this.takeStockInputs.value[index],
          };

        if (this.takeStockNotes.value[index]) {
          item.note = this.takeStockNotes.value[index];
        }

        changedItems.push(item);
      }

      index++;
    }

    return changedItems;
  }

  takeFromTrunk() {
    const changedItems = this.getChangedItemsInTrunk();

    // Ohne Menge gibt es nichts abzubuchen; vorher lehnte die API die leere Liste mit einer englischen Meldung ab.
    // Eingegebene Kommentare bleiben dabei erhalten.
    if (changedItems.length === 0) {
      this.notificationService.createNotification({
        title: 'Keine Menge eingetragen',
        text: 'Bitte tragen Sie bei mindestens einem Artikel die Menge ein, die abgebucht werden soll.',
        status: 'warning',
        dismissed: 0,
      });
      return;
    }

    this.takeStockInputs.next([]);
    this.takeStockNotes.next([]);

    this.store.dispatch(changeItemsInTrunk({ changedItems }));
  }

  takeAllFromTrunk() {
    let i = 0;
    const trunkArticles = this.dataSource.connect().value as any;

    for (const trunkArticle of trunkArticles) {
      this.takeStockInputs.value[i] = trunkArticle.availableStock;

      i++;
    }
  }

  closeTrunk() {
    this.closeTrunkClick.emit();

    const withdrawnStockChanges = this.getChangedItemsInTrunk();

    this.store.dispatch(closeTrunk({ withdrawnStockChanges }));
  }

  toggleExpand(element: any) {
    const elementIndex = this.expandedElements.findIndex(
      (expandedElement) => expandedElement.id === element.id
    );

    if (elementIndex > -1) {
      this.expandedElements.splice(elementIndex, 1);
      this.allRowsExpanded = false;
    } else {
      this.expandedElements.push(element);

      if (
        this.expandedElements.length === this.dataSource.connect().value.length
      ) {
        this.allRowsExpanded = true;
      }
    }
  }

  toggleAllExpanded() {
    if (
      this.expandedElements.length === this.dataSource.connect().value.length
    ) {
      this.expandedElements = [];
      this.allRowsExpanded = false;
    } else {
      this.expandedElements = [...this.dataSource.connect().value];
      this.allRowsExpanded = true;
    }
  }

  filterStorageLocations() {
    this.storageLocationSearchTerm = this.storageLocationFilterCtrl.value ?? '';

    this.storageLocationService
      .loadLocations(
        '?page=1&limit=10000&search=' +
          this.storageLocationSearchTerm.toLowerCase()
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            items: response.items.filter(
              (storageLocation) =>
                storageLocation.id !== this.selectedStorageLocation.value
            ),
          };
        })
      )
      .subscribe((res) => {
        this.filteredStorageLocations.next(res.items);
      });
  }
}

/**
 * Rundet eine Menge auf den nächsten Entnahmeschritt auf, höchstens aber auf den größten
 * verfügbaren Schritt (entspricht normalizeAmount der Lagerort-Ansicht). Ungültige Mengen ergeben null.
 */
export function normalizeStockAmount(
  value: number,
  samplingStep: number,
  availableStock: number
): number | null {
  const step = samplingStep > 0 ? samplingStep : 1;

  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  const amount = Math.min(
    calculateNextIntervalStep(value, step),
    Math.floor(availableStock / step) * step
  );

  return amount > 0 ? amount : null;
}
